<template>
	<div class="auth-layout">
		<div class="auth-visual">
			<div class="circuit-bg" aria-hidden="true">
				<svg class="circuit-svg" viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg">
					<circle cx="200" cy="200" r="178" fill="none" stroke="currentColor" stroke-width="1.2" opacity="0.18" />
					<circle cx="200" cy="200" r="138" fill="none" stroke="currentColor" stroke-width="1" opacity="0.12" />
					<path d="M60 120 Q120 80 160 140 T240 160 T320 100" fill="none" stroke="currentColor" stroke-width="1.5" opacity="0.22" />
					<path d="M70 280 Q130 240 180 290 T280 270 T350 320" fill="none" stroke="currentColor" stroke-width="1.5" opacity="0.2" />
					<circle cx="160" cy="140" r="4" fill="currentColor" opacity="0.4" />
					<circle cx="240" cy="160" r="4" fill="currentColor" opacity="0.4" />
					<circle cx="180" cy="290" r="4" fill="currentColor" opacity="0.35" />
					<circle cx="280" cy="270" r="4" fill="currentColor" opacity="0.35" />
				</svg>
			</div>
			<div class="visual-content">
				<div class="logo-ring">
					<img src="@/assets/common/logo.png" class="logo" alt="新通" />
				</div>
				<h1 class="brand-title">{{ brandTitle }}</h1>
				<p class="brand-desc">{{ brandDesc }}</p>
				<div class="feature-tags">
					<span v-for="tag in tags" :key="tag" class="feature-tag">{{ tag }}</span>
				</div>
			</div>
		</div>
		<div class="auth-panel">
			<div class="auth-card">
				<div class="card-header">
					<h2 class="card-title">{{ title }}</h2>
					<p v-if="subtitle" class="card-subtitle">{{ subtitle }}</p>
				</div>
				<slot></slot>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'AuthLayout',
	props: {
		title: {
			type: String,
			required: true,
		},
		subtitle: {
			type: String,
			default: '',
		},
		brandTitle: {
			type: String,
			default: '新通',
		},
		brandDesc: {
			type: String,
			default: '连接你我 · 沟通无界',
		},
		tags: {
			type: Array,
			default: () => ['即时通讯', '安全可靠', '高效协作'],
		},
	},
};
</script>

<style scoped lang="scss">
$auth-primary: #e93738;
$auth-primary-dark: #c92c2d;
$auth-primary-light: #f15a5a;
$auth-bg: #fff8f8;

.auth-layout {
	display: flex;
	width: 100%;
	height: 100%;
	min-height: 100vh;
	background: $auth-bg;
	overflow: hidden;
}

.auth-visual {
	position: relative;
	flex: 0 0 44%;
	display: flex;
	align-items: center;
	justify-content: center;
	background: linear-gradient(145deg, $auth-primary-dark 0%, $auth-primary 48%, $auth-primary-light 100%);
	color: #fff;
	overflow: hidden;

	&::before {
		content: '';
		position: absolute;
		inset: 0;
		background:
			radial-gradient(circle at 18% 82%, rgba(255, 255, 255, 0.12) 0%, transparent 45%),
			radial-gradient(circle at 82% 18%, rgba(0, 0, 0, 0.12) 0%, transparent 40%);
		pointer-events: none;
	}

	.circuit-bg {
		position: absolute;
		inset: -8%;
		display: flex;
		align-items: center;
		justify-content: center;
		color: #fff;
		animation: circuit-drift 32s linear infinite;

		.circuit-svg {
			width: 118%;
			height: 118%;
		}
	}

	.visual-content {
		position: relative;
		z-index: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		padding: 48px 40px;
		text-align: center;
		max-width: 420px;
		animation: fade-up 0.7s ease-out;
	}

	.logo-ring {
		width: 148px;
		height: 148px;
		border-radius: 50%;
		background: rgba(255, 255, 255, 0.14);
		border: 2px solid rgba(255, 255, 255, 0.28);
		display: flex;
		align-items: center;
		justify-content: center;
		margin-bottom: 28px;
		box-shadow:
			0 0 0 14px rgba(255, 255, 255, 0.06),
			0 18px 48px rgba(0, 0, 0, 0.22);
		animation: logo-pulse 4.2s ease-in-out infinite;

		.logo {
			width: 112px;
			height: 112px;
			border-radius: 50%;
			object-fit: contain;
			background: #fff;
		}
	}

	.brand-title {
		margin: 0 0 12px;
		font-size: 36px;
		font-weight: 700;
		letter-spacing: 8px;
		text-shadow: 0 2px 10px rgba(0, 0, 0, 0.18);
	}

	.brand-desc {
		margin: 0 0 28px;
		font-size: 15px;
		line-height: 1.6;
		opacity: 0.9;
		letter-spacing: 2px;
	}

	.feature-tags {
		display: flex;
		flex-wrap: wrap;
		gap: 10px;
		justify-content: center;

		.feature-tag {
			padding: 6px 16px;
			border-radius: 20px;
			font-size: 13px;
			background: rgba(255, 255, 255, 0.14);
			border: 1px solid rgba(255, 255, 255, 0.24);
			backdrop-filter: blur(4px);
		}
	}
}

.auth-panel {
	position: relative;
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 40px 32px;
	background:
		radial-gradient(circle at 88% 12%, rgba($auth-primary, 0.08) 0%, transparent 42%),
		radial-gradient(circle at 18% 88%, rgba($auth-primary, 0.05) 0%, transparent 38%),
		linear-gradient(160deg, #fff 0%, $auth-bg 100%);
}

.auth-card {
	position: relative;
	width: 100%;
	max-width: 440px;
	padding: 40px 36px 32px;
	background: #fff;
	border-radius: 20px;
	box-shadow:
		0 8px 32px rgba($auth-primary, 0.1),
		0 1px 3px rgba(0, 0, 0, 0.04);
	border: 1px solid rgba($auth-primary, 0.1);
	animation: fade-up 0.55s ease-out 0.08s both;

	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: 36px;
		right: 36px;
		height: 3px;
		border-radius: 0 0 3px 3px;
		background: linear-gradient(90deg, $auth-primary-dark, $auth-primary-light);
	}

	.card-header {
		margin-bottom: 28px;
		text-align: center;
	}

	.card-title {
		margin: 0 0 6px;
		font-size: 24px;
		font-weight: 600;
		color: $auth-primary-dark;
		letter-spacing: 2px;
	}

	.card-subtitle {
		margin: 0;
		font-size: 14px;
		color: #999;
	}
}

@keyframes circuit-drift {
	0% { transform: rotate(0deg); }
	100% { transform: rotate(360deg); }
}

@keyframes logo-pulse {
	0%, 100% {
		box-shadow: 0 0 0 14px rgba(255, 255, 255, 0.06), 0 18px 48px rgba(0, 0, 0, 0.22);
	}
	50% {
		box-shadow: 0 0 0 18px rgba(255, 255, 255, 0.1), 0 22px 56px rgba(0, 0, 0, 0.28);
	}
}

@keyframes fade-up {
	from {
		opacity: 0;
		transform: translateY(16px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@media (max-width: 900px) {
	.auth-layout {
		flex-direction: column;
		overflow-y: auto;
	}

	.auth-visual {
		flex: none;
		min-height: 220px;
		padding: 28px 20px;

		.visual-content {
			padding: 16px;
		}

		.logo-ring {
			width: 96px;
			height: 96px;
			margin-bottom: 14px;

			.logo {
				width: 72px;
				height: 72px;
			}
		}

		.brand-title {
			font-size: 24px;
			letter-spacing: 4px;
		}

		.brand-desc,
		.feature-tags {
			display: none;
		}
	}

	.auth-panel {
		flex: 1;
		padding: 24px 16px 40px;
	}

	.auth-card {
		padding: 28px 22px 24px;
	}
}
</style>
